import { Injectable } from '@angular/core';
import { Region } from '../shared/models/enums';
import { environment } from '../../environments/environment';
import { CrossDomainStateService } from './cross-domain-state.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  private redirecting = false;

  constructor(
    private crossDomainStateService: CrossDomainStateService,
    private log: LoggerService,
  ) {}

  /** True only once a region navigation has actually been handed to the browser. */
  public get isRedirecting(): boolean {
    return this.redirecting;
  }

  public detectRegion(): Region {
    const host = window.location.host;
    const map = environment.cpRegionHostmap;
    for (const region of Object.keys(map) as Region[]) {
      const regionHost = (map[region] || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
      if (regionHost === host) {
        return region;
      }
    }
    return Region.GLOBAL;
  }

  /**
   * The `emailregion` flag alone is not enough — the backend sets it for the account's own region
   * too — so the host decides, or a caller waits on a navigation that never happens.
   */
  public isOtherRegion(region: Region, redirect: 0 | 1): boolean {
    if (!redirect) {
      return false;
    }
    if (!environment.production && this.currentHost.indexOf('cp-local') > -1) {
      return false;
    }
    const targetHost = environment.cpRegionHostmap[region] ?? '';
    return Boolean(targetHost) && window.location.origin !== targetHost;
  }

  private get currentHost(): string {
    return window.location.host;
  }

  public redirectToRegion(region: Region, addRegionConflict = false): void {
    const currentHost = window.location.origin;
    const isLink = window.location.pathname.startsWith('/dl/');
    let targetHost = environment.cpRegionHostmap[region];
    if (isLink) {
      targetHost = environment.linkRegionHostmap[region];
    }
    // Both of these leave the caller on the current page. They must not report a redirect, or a
    // caller that stops work to await a navigation waits for one that is never coming.
    if (!targetHost) {
      this.log.error(`#region: no host mapped for region=${region}, staying put`);
      return;
    }
    if (currentHost === targetHost) {
      this.log.warn(`#region: already on the host for region=${region}, no redirect`);
      return;
    }

    const newUrl = window.location.href.replace(currentHost, targetHost);
    const url = new URL(newUrl);
    if (addRegionConflict) {
      url.searchParams.set('regionconflict', '1');
    }

    this.redirecting = true;
    this.crossDomainStateService.preserveStates(url, () => {
      window.location.replace(url.toString());
    });
  }
}
